import { graphql } from "@__generated__/gql";

export const GET_AUTHENTICATED_ITEM = graphql(`
  query GetAuthenticatedItem {
    authenticatedItem {
      ... on User {
        id
        email
        isAdmin
        mustChangePassword
        firstName
        lastName
        title
        credentials
        avatarUrl
      }
    }
  }
`);

export const FIND_REPORT_TYPE_BY_NAME = graphql(`
  query FindReportTypeByName($name: String!) {
    reportTypes(where: { name: { equals: $name } }, take: 1) {
      id
    }
  }
`);

export const GET_SETTINGS = graphql(`
  query GetSettings {
    settings {
      id
      googleApiKey
      areaOfOriginOptions
      pointOfOriginOptions
      firstFuelOptions
      ignitionSourceOptions
      indicatorsOptions
      technicalTermsOptions
      attendeeOrganisationOptions
      attendeeRoleOptions
      witnessRelationshipOptions
      witnessEventOptions
      defaultSiteAccessText
      initialSiteObservationOptions
      preliminaryFireAssessmentOptions
      evidenceCategoryOptions
      insurerOptions
      brokerOptions
      defaultWeatherLeadInDays
    }
  }
`);

const REPORT_LIST_FIELDS_FRAGMENT = graphql(`
  fragment ReportListFields on Report {
    id
    code
    client
    reportType {
      name
    }
    stage {
      name
    }
    status {
      name
    }
    propertyInformation {
      riskAddress
      latitude
      longitude
    }
    assessor {
      firstName
      lastName
    }
    reviews {
      reviewers {
        firstName
        lastName
      }
    }
    dateOfLoss
    dateOfInspection
    insurer
    broker
    created_at
  }
`);

export const GET_ALL_REPORTS = graphql(`
  query GetAllReports {
    reports(orderBy: [{ created_at: desc }]) {
      ...ReportListFields
    }
  }
`);

export const GET_ALL_REPORTS_PAGINATED = graphql(`
  query GetAllReportsPaginated($take: Int, $skip: Int) {
    reports(orderBy: [{ created_at: desc }], take: $take, skip: $skip) {
      ...ReportListFields
    }
    reportsCount
  }
`);

const REPORT_REVIEW_FIELDS_FRAGMENT = graphql(`
  fragment ReportReviewFields on ReportReview {
    id
    clientInformation
    circumstancesSummary
    witnessStatements
    siteAccess
    weatherConditions
    executiveSummary
    scope
    keyConclusions
    recommendations
    otherFactorsConsidered
    overallComments
    status
    submittedAt
    reviewers {
      id
      firstName
      lastName
      email
    }
    notifiedReviewers {
      id
    }
  }
`);

export const GET_REPORT_BY_ID = graphql(`
  query GetReportById($id: ID!) {
    report(where: { id: $id }) {
      id
      code
      client
      insurer
      broker
      asset
      dateOfLoss
      dateOfInspection
      evidence
      weatherCondition
      weatherLeadInDays
      created_at
      reportType {
        id
        name
      }
      stage {
        id
        name
      }
      status {
        id
        name
      }
      assessor {
        id
        firstName
        lastName
        email
      }
      propertyInformation {
        id
        riskAddress
        inspectionAddress
      }
      references {
        id
        claimNumber
        jobNumber
        refNumber
      }
      witnessInterviews {
        id
        name
        witnessRelationship
        event
        interviewDatetime
        briefNote
        detailedNote
      }
      attendees
      reviews {
        ...ReportReviewFields
      }
      summary {
        id
        executiveSummary
        scope
        scopeItems
        keyConclusions
        recommendations
      }
      analysis {
        id
        circumstanceSummary
        observation
        narrative
        otherFactorsConsidered
      }
      fieldWork {
        id
        siteAccessAndSafety
        initialSiteObservations
        preliminaryFireAssessment
      }
    }
  }
`);

export const GET_ALL_REPORT_STATUSES = graphql(`
  query GetAllReportStatuses {
    reportStatuses {
      id
      name
    }
  }
`);

export const GET_ALL_REPORT_STAGES = graphql(`
  query GetAllReportStages {
    reportStages {
      id
      name
    }
  }
`);

export const GET_ALL_USERS = graphql(`
  query GetAllUsers {
    users {
      id
      firstName
      lastName
      email
      title
    }
  }
`);

export const GET_ALL_REPORT_TYPES = graphql(`
  query GetAllReportTypes {
    reportTypes {
      id
      name
    }
  }
`);

export const SEARCH_REPORTS = graphql(`
  query SearchReports($where: ReportWhereInput!, $take: Int, $skip: Int) {
    reports(where: $where, orderBy: [{ created_at: desc }], take: $take, skip: $skip) {
      id
      code
      client
      reportType {
        name
      }
      stage {
        name
      }
      status {
        name
      }
      propertyInformation {
        riskAddress
      }
      assessor {
        firstName
        lastName
      }
      reviews {
        reviewers {
          firstName
          lastName
        }
      }
      dateOfLoss
      dateOfInspection
      insurer
      broker
      created_at
    }
    reportsCount(where: $where)
  }
`);

export const GET_DASHBOARD_STATS = graphql(`
  query GetDashboardStats($completedMonthStart: DateTime!) {
    activeCount: reportsCount(where: { status: { name: { equals: "Active" } } })
    completedThisMonthCount: reportsCount(
      where: {
        status: { name: { equals: "Completed" } }
        created_at: { gte: $completedMonthStart }
      }
    )
    pendingReviewCount: reportsCount(
      where: { status: { name: { equals: "Active" } }, stage: { name: { equals: "Under Review" } } }
    )
    completedReports: reports(
      where: { status: { name: { equals: "Completed" } }, completedAt: { not: null } }
    ) {
      id
      created_at
      completedAt
    }
    recentReports: reports(orderBy: [{ created_at: desc }], take: 5) {
      id
      code
      client
      created_at
      status {
        name
      }
      stage {
        name
      }
    }
  }
`);

export const GET_ACTIVE_REPORTS = graphql(`
  query GetActiveReports {
    reports(where: { status: { name: { equals: "Active" } } }, orderBy: [{ created_at: desc }]) {
      id
      code
      client
      reportType {
        name
      }
      stage {
        name
      }
      status {
        name
      }
      propertyInformation {
        riskAddress
      }
      assessor {
        id
        firstName
        lastName
      }
      reviews {
        reviewers {
          id
          firstName
          lastName
        }
      }
      dateOfLoss
      dateOfInspection
      insurer
      broker
      created_at
    }
  }
`);
